import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

export type Evidence = Database['public']['Tables']['evidence']['Row'];

interface EvidenceFilters {
  relationType?: string;
  relationId?: string;
}

interface CreateEvidenceData {
  name: string;
  type?: string;
  storage_path?: string;
  file_size?: number;
  notes?: string;
  related_to_type?: string;
  related_to_id?: string;
  owner_id?: string;
  org_id: string;
}

export const useEvidence = (orgId?: string, filters?: EvidenceFilters) => {
  const queryClient = useQueryClient();

  const evidenceQuery = useQuery({
    queryKey: ['evidence', orgId, filters?.relationType, filters?.relationId],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('evidence')
        .select('*, owner:profiles!fk_evidence_owner(name, email)')
        .eq('org_id', orgId);

      if (filters?.relationType && filters?.relationId) {
        query = query
          .eq('related_to_type', filters.relationType)
          .eq('related_to_id', filters.relationId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return data ?? [];
    },
    enabled: !!orgId,
  });

  const createEvidenceMutation = useMutation({
    mutationFn: async (data: CreateEvidenceData) => {
      const evidenceName = data.name?.trim();
      if (!evidenceName) {
        throw new Error('Evidence name is required');
      }

      const { data: created, error } = await supabase.from('evidence').insert([{
        org_id: data.org_id,
        name: evidenceName,
        type: data.type ?? 'document',
        storage_path: data.storage_path ?? null,
        file_size: data.file_size ?? null,
        notes: data.notes ?? null,
        related_to_type: data.related_to_type ?? null,
        related_to_id: data.related_to_id ?? null,
        owner_id: data.owner_id ?? null,
      }]).select('id').single();
      
      if (error) throw error;
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence'] });
      toast.success('Evidence added successfully');
    },
    onError: (error) => {
      console.error('Error adding evidence:', error);
      toast.error('Failed to add evidence');
    },
  });

  const deleteEvidenceMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the evidence
      const { error } = await supabase.from('evidence').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Evidence deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting evidence:', error);
      toast.error('Failed to delete evidence');
    },
  });

  const updateEvidenceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateEvidenceData> }) => {
      const { error } = await supabase
        .from('evidence')
        .update({
          name: data.name,
          type: data.type,
          notes: data.notes,
          owner_id: data.owner_id ?? null,
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence'] });
      toast.success('Evidence updated successfully');
    },
    onError: (error) => {
      console.error('Error updating evidence:', error);
      toast.error('Failed to update evidence');
    },
  });

  return {
    evidence: evidenceQuery.data ?? [],
    isLoading: evidenceQuery.isLoading,
    error: evidenceQuery.error,
    createEvidence: createEvidenceMutation.mutate,
    deleteEvidence: deleteEvidenceMutation.mutate,
    updateEvidence: updateEvidenceMutation.mutate,
    isCreating: createEvidenceMutation.isPending,
    isDeleting: deleteEvidenceMutation.isPending,
    isUpdating: updateEvidenceMutation.isPending,
  };
};
